/**
 * How long `useSfuJoin` waits before trying to build a media session again.
 *
 * Two waits, for two different failures.
 *
 * A JOIN that fails — the orchestrator answering 502/503 because no SFU node
 * took the peer, a node refusing the WS registration, a fetch that never got
 * an answer — used to be final: the hook set its error and never tried again.
 * On 2026-10-06 (1,000-bot meeting) a signaling overload tore down ~5,900 SFU
 * sessions at once; the orchestrator, marking busy SFUs dead, refused 248 of
 * the rebuilds, and ~534 participants sat in the meeting with a roster tile and
 * no media session until they reloaded. A failed join now retries on capped
 * exponential backoff: 2 s, 4 s, 8 s … capped at 30 s.
 *
 * A REBUILD that keeps failing (MAX_REJOINS network-driven rebuilds in a row)
 * still shows its error — the user must not stare at a call that looks fine
 * and reaches nobody — but no longer gives up for good: it tries once more
 * after a slow wait, and that attempt starts a fresh run of quick rebuilds.
 *
 * Every delay is jittered between half and all of the step ("equal jitter"),
 * the same rule as the signaling socket's reconnect (signaling-reconnect.ts).
 * The failures this answers are fleet-wide — one overloaded orchestrator, one
 * dead node — so without jitter every client that lost its session together
 * would come back together, in lockstep, and knock it over again.
 */

/** The first retry's step: the wait is 1–2 s after one failed join. */
export const SFU_JOIN_RETRY_BASE_MS = 2_000

/** No failed join waits longer than this (15–30 s once jittered). */
export const SFU_JOIN_RETRY_CAP_MS = 30_000

/** After MAX_REJOINS rebuilds in a row: one slow retry, 30–60 s once jittered. */
export const SFU_REBUILD_SLOW_RETRY_MS = 60_000

function equalJitter(step: number, random: () => number): number {
  return step / 2 + random() * (step / 2)
}

/**
 * The wait before the next join, given how many joins in a row have now
 * failed (1 for the first failure). `random` is injectable for tests.
 */
export function sfuJoinRetryDelayMs(failures: number, random: () => number = Math.random): number {
  const step = Math.min(SFU_JOIN_RETRY_BASE_MS * 2 ** Math.max(0, failures - 1), SFU_JOIN_RETRY_CAP_MS)
  return equalJitter(step, random)
}

/** The wait before the one slow retry once the rebuild cap is reached. */
export function sfuSlowRetryDelayMs(random: () => number = Math.random): number {
  return equalJitter(SFU_REBUILD_SLOW_RETRY_MS, random)
}
